<template>
  <div class="h-full flex flex-col min-h-0">
    <!-- Back (small screens only) -->
    <div v-if="showBack" class="flex-shrink-0 flex items-center gap-2 px-3 h-14 bg-white dark:bg-[#1C2128] border-b border-gray-200 dark:border-gray-700 lg:hidden">
      <button type="button" @click="$emit('back')" aria-label="Back to jobs"
        class="w-9 h-9 flex items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800">
        <svg class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5"/></svg>
      </button>
      <span class="font-semibold text-gray-900 dark:text-white">Job details</span>
    </div>

    <!-- Nothing chosen -->
    <div v-if="!job && !loading && !error" class="flex-1 flex flex-col items-center justify-center text-center px-6 py-24">
      <div class="w-16 h-16 rounded-2xl bg-white dark:bg-[#1C2128] border border-gray-200 dark:border-gray-700 shadow-sm flex items-center justify-center mb-4">
        <svg class="w-8 h-8 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" stroke-width="1.25" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M20.25 14.15v4.25c0 1.094-.787 2.036-1.872 2.18-2.087.277-4.216.42-6.378.42s-4.291-.143-6.378-.42c-1.085-.144-1.872-1.086-1.872-2.18v-4.25m16.5 0a2.18 2.18 0 0 0 .75-1.661V8.706c0-1.081-.768-2.015-1.837-2.175a48.114 48.114 0 0 0-3.413-.387m4.5 8.006c-.194.165-.42.295-.673.38A23.978 23.978 0 0 1 12 15.75c-2.648 0-5.195-.429-7.577-1.22a2.016 2.016 0 0 1-.673-.38m0 0A2.18 2.18 0 0 1 3 12.489V8.706c0-1.081.768-2.015 1.837-2.175a48.111 48.111 0 0 1 3.413-.387m7.5 0V5.25A2.25 2.25 0 0 0 13.5 3h-3a2.25 2.25 0 0 0-2.25 2.25v.894m7.5 0a48.667 48.667 0 0 0-7.5 0"/></svg>
      </div>
      <p class="font-semibold text-gray-700 dark:text-gray-200">Select a job</p>
      <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">Choose one from the list to read it here.</p>
    </div>

    <!-- Loading -->
    <div v-else-if="loading" class="flex-1 overflow-y-auto animate-pulse" aria-hidden="true">
      <div class="max-w-3xl mx-auto px-5 sm:px-6">
        <div class="flex gap-3.5 pt-5 pb-5">
          <div class="w-11 h-11 rounded-xl bg-gray-200 dark:bg-gray-700 flex-shrink-0"></div>
          <div class="flex-1 space-y-2.5 pt-0.5">
            <div class="h-5 w-3/4 rounded bg-gray-200 dark:bg-gray-700"></div>
            <div class="h-3.5 w-1/3 rounded bg-gray-100 dark:bg-gray-800"></div>
            <div class="h-3.5 w-2/3 rounded bg-gray-100 dark:bg-gray-800 mt-3"></div>
          </div>
        </div>
        <hr class="border-gray-200 dark:border-gray-700">
        <div class="py-5 space-y-3">
          <div class="h-4 w-28 rounded bg-gray-200 dark:bg-gray-700 mb-4"></div>
          <div v-for="n in 7" :key="n" class="h-3.5 rounded bg-gray-100 dark:bg-gray-800" :style="{ width: `${96 - n * 5}%` }"></div>
        </div>
      </div>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="flex-1 flex flex-col items-center justify-center py-24 text-center px-4">
      <div class="w-14 h-14 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mb-3">
        <svg class="w-7 h-7 text-red-500" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>
      </div>
      <h3 class="text-base font-semibold text-gray-900 dark:text-white">Something went wrong</h3>
      <p class="text-sm text-gray-500 dark:text-gray-400 mb-4">{{ error }}</p>
      <button @click="$emit('retry')" class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold">Try again</button>
    </div>

    <template v-else>
      <div ref="scroller" @scroll.passive="onScroll" class="flex-1 overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <!-- The job's title and company stay in view while the rest scrolls under them. Once the page is scrolled this shrinks, with
             a short animation: the logo and title get smaller and the company line folds away, so more of the job is left to read.
             Nothing moves for people who prefer less motion. -->
        <div class="sticky top-0 z-10 bg-white dark:bg-[#1C2128] border-b border-gray-200 dark:border-gray-700" :class="compact ? 'shadow-sm' : 'shadow-none'"
          style="transition: box-shadow 300ms ease-out">
          <div class="max-w-3xl mx-auto px-5 sm:px-6 transition-[padding] duration-300 ease-out motion-reduce:transition-none" :class="compact ? 'py-2' : 'py-4'">
            <div class="flex items-start gap-3.5">
              <!-- With the company stored (company details on), its logo and name open its details in a panel -->
              <button v-if="companyId" type="button" @click="$emit('open-company', companyId)" :aria-label="`Show details of ${job.company_name || 'the company'}`"
                class="flex-shrink-0 self-start rounded-xl transition-transform hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50">
                <JobAvatar :name="job.company_name" :src="job.company_logo_url" :class="avatarClass" />
              </button>
              <JobAvatar v-else :name="job.company_name" :src="job.company_logo_url" :class="avatarClass" />
              <div class="min-w-0 flex-1">
                <h2 class="font-semibold tracking-tight text-gray-900 dark:text-white transition-[font-size,line-height] duration-300 ease-out motion-reduce:transition-none"
                  :class="compact ? 'text-base leading-8 truncate' : 'text-lg sm:text-xl leading-7'">{{ job.title || 'Untitled job' }}</h2>

                <!-- Second row: the company, and the job's actions in front of it at the far end (icons only, the name shows on hover).
                     It folds shut by animating its row from its full height to nothing. -->
                <div class="grid transition-[grid-template-rows,opacity] duration-300 ease-out motion-reduce:transition-none"
                  :class="compact ? 'grid-rows-[0fr] opacity-0' : 'grid-rows-[1fr] opacity-100'" :aria-hidden="compact" :inert="compact">
                  <div class="min-h-0" :class="clip ? 'overflow-hidden' : ''">
                    <div class="flex flex-wrap items-center gap-x-3 gap-y-1.5 mt-0.5">
                      <p class="min-w-0 text-sm text-gray-600 dark:text-gray-300">
                        <button v-if="companyId" type="button" @click="$emit('open-company', companyId)"
                          class="font-medium text-blue-600 dark:text-blue-400 hover:underline text-left">{{ job.company_name }}</button>
                        <a v-else-if="job.company.url" :href="job.company.url" target="_blank" rel="noopener noreferrer" data-turbo="false"
                          class="font-medium text-blue-600 dark:text-blue-400 hover:underline">{{ job.company_name }}</a>
                        <span v-else-if="job.company_name" class="font-medium text-gray-800 dark:text-gray-100">{{ job.company_name }}</span>
                      </p>

                      <span v-if="details?.blocked_at" title="You blocked this company, so its jobs are hidden from the list"
                        class="inline-flex items-center h-5 px-2 rounded-full text-[11px] font-semibold bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 ring-1 ring-red-200 dark:ring-red-800">Blocked</span>
                      <span v-if="details?.kind === 'recruiter'" title="You marked this company as a recruiter"
                        class="inline-flex items-center h-5 px-2 rounded-full text-[11px] font-semibold bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-300 ring-1 ring-violet-200 dark:ring-violet-800">Recruiter</span>
                      <span v-if="alsoApplied.length" :title="`You applied to: ${appliedHint}`"
                        class="inline-flex items-center gap-1 h-5 px-2 rounded-full text-[11px] font-semibold bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 ring-1 ring-amber-200 dark:ring-amber-800">
                        <svg class="w-3 h-3" fill="none" stroke="currentColor" stroke-width="3" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5"/></svg>
                        Already applied here
                      </span>

                      <div class="ml-auto flex items-center gap-1">
                        <IconButton v-if="job.url" size="sm" tone="primary" align="right" :href="job.url" :icon="ICONS.external" label="Open on LinkedIn" />
                        <IconButton size="sm" :tone="job.applied_at ? 'success' : 'default'" :icon="ICONS.check" :pressed="Boolean(job.applied_at)" :disabled="applying" align="right"
                          :label="job.applied_at ? `Applied on ${appliedOn}. Click to undo` : 'Mark as applied'" @click="$emit('toggle-applied')" />
                        <span class="hidden sm:block w-px h-4 bg-gray-200 dark:bg-gray-700 mx-0.5" aria-hidden="true"></span>
                        <JobMarkButton kind="bookmark" bar small :active="Boolean(job.bookmarked_at)" @click="$emit('toggle-bookmark', job)" />
                        <JobMarkButton kind="ignore" bar small :active="Boolean(job.ignored_at)" @click="$emit('toggle-ignore', job)" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <article class="max-w-3xl mx-auto px-5 sm:px-6 pb-8">
          <!-- The facts about the job -->
          <header class="pt-4 pb-5">
            <dl class="grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-3 text-sm">
              <div v-if="job.location" class="flex items-center gap-2 min-w-0">
                <svg class="w-4 h-4 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"/><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z"/></svg>
                <dt class="sr-only">Location</dt><dd class="truncate text-gray-700 dark:text-gray-200">{{ job.location }}</dd>
              </div>
              <div v-if="posted" class="flex items-center gap-2">
                <svg class="w-4 h-4 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>
                <dt class="sr-only">Posted</dt><dd class="text-gray-700 dark:text-gray-200">{{ posted }}</dd>
              </div>
              <div v-if="job.applicants" class="flex items-center gap-2">
                <svg class="w-4 h-4 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z"/></svg>
                <dt class="sr-only">Applicants</dt><dd class="text-gray-700 dark:text-gray-200">{{ job.applicants }}</dd>
              </div>
            </dl>

            <div v-if="job.easy_apply || job.preferences.length" class="flex flex-wrap gap-2 mt-3">
              <span v-if="job.easy_apply" :class="pill('blue')">Easy Apply</span>
              <span v-for="preference in job.preferences" :key="preference" :class="pill('gray')">{{ preference }}</span>
            </div>

            <!-- Another job at this company was already applied to -->
            <div v-if="alsoApplied.length" class="mt-4 flex gap-2.5 rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/15 px-3.5 py-3" role="status">
              <svg class="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.008v.008H12v-.008Z"/></svg>
              <div class="min-w-0 text-[13px] leading-relaxed text-amber-900 dark:text-amber-200">
                <p>
                  <strong class="font-semibold">You already applied to {{ alsoApplied.length === 1 ? 'another job' : `${alsoApplied.length} other jobs` }} at {{ job.company_name || 'this company' }}</strong>
                  <template v-if="alsoAppliedOn">, most recently on {{ alsoAppliedOn }}</template>.
                </p>
                <ul class="mt-1 space-y-0.5">
                  <li v-for="item in alsoApplied.slice(0, 3)" :key="item.id" class="truncate">
                    <button type="button" @click="openOther(item.id)" class="font-medium underline decoration-amber-400/60 hover:decoration-amber-600 text-left">{{ item.title || 'Untitled job' }}</button>
                  </li>
                </ul>
                <p class="mt-1.5 text-xs text-amber-800/90 dark:text-amber-300/90">Several applications to one company, each with a different resume, can look scattered. Make sure this one is worth it.</p>
              </div>
            </div>
          </header>

          <hr class="border-gray-200 dark:border-gray-700">

          <!-- About the job -->
          <section aria-labelledby="about-job" class="py-5">
            <div class="flex items-baseline justify-between gap-3 mb-3">
              <h3 id="about-job" class="text-sm font-semibold text-gray-900 dark:text-white">About the job</h3>
              <div class="flex items-center gap-3">
                <span v-if="readingMinutes" class="text-xs text-gray-400 dark:text-gray-500 tabular-nums">{{ readingMinutes }} min read</span>
                <!-- Copies the job's title and its whole description, not only the part showing -->
                <button v-if="blocks.length" type="button" @click="copyDescription" :title="copyFailed ? 'Could not copy' : 'Copy the title and the full description'"
                  class="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-lg border text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50"
                  :class="copied
                    ? 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400'
                    : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1C2128] text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'">
                  <svg v-if="copied" class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5"/></svg>
                  <svg v-else class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 0 1-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 0 1 1.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 0 0-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 0 1-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 0 0-3.375-3.375h-1.5a1.125 1.125 0 0 1-1.125-1.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H9.75"/></svg>
                  {{ copied ? 'Copied' : copyFailed ? 'Could not copy' : 'Copy' }}
                </button>
              </div>
            </div>

            <div>
              <p v-if="!blocks.length" class="text-sm text-gray-400 dark:text-gray-500">No description was collected for this job.</p>
              <div v-else class="relative">
                <div class="space-y-6 text-[14px] leading-7 font-normal text-gray-600 dark:text-gray-300 break-words overflow-hidden"
                  :class="collapsed ? 'max-h-80' : ''">
                  <div v-for="(group, groupIndex) in groups" :key="groupIndex" class="space-y-3">
                    <h4 v-if="group.heading" class="text-[13px] font-semibold tracking-[0.01em] text-gray-900 dark:text-gray-100">{{ group.heading }}</h4>
                    <template v-for="(block, index) in group.blocks" :key="index">
                      <ul v-if="block.type === 'list'" class="space-y-2">
                        <li v-for="(item, itemIndex) in block.items" :key="itemIndex" class="flex gap-3">
                          <span class="mt-[0.72em] w-1 h-1 rounded-full bg-gray-300 dark:bg-gray-600 flex-shrink-0" aria-hidden="true"></span>
                          <span class="min-w-0">{{ item }}</span>
                        </li>
                      </ul>
                      <p v-else>{{ block.text }}</p>
                    </template>
                  </div>
                </div>
                <div v-if="collapsed" class="absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-white dark:from-[#1C2128] via-white/80 dark:via-[#1C2128]/80 to-transparent pointer-events-none"></div>
              </div>

              <div v-if="long" class="mt-4 flex justify-center">
                <button type="button" @click="expanded = !expanded" :aria-expanded="expanded"
                  class="inline-flex items-center gap-1.5 h-8 px-3.5 rounded-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1C2128] text-[13px] font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                  {{ expanded ? 'Show less' : 'Show full description' }}
                  <svg class="w-3.5 h-3.5 text-gray-400 transition-transform" :class="expanded ? 'rotate-180' : ''" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5"/></svg>
                </button>
              </div>
            </div>
          </section>


          <hr v-if="hasCompany || job.hiring_team" class="border-gray-200 dark:border-gray-700">

          <!-- Company and hiring team, side by side -->
          <div v-if="hasCompany || job.hiring_team" class="py-5 grid gap-x-8 gap-y-6" :class="hasCompany && job.hiring_team ? 'md:grid-cols-2' : ''">
            <section v-if="hasCompany" aria-labelledby="about-company">
              <h3 id="about-company" class="text-sm font-semibold text-gray-900 dark:text-white mb-3">About the company</h3>

              <!-- The stored company: its logo, where to find it, and the colours of its website -->
              <div v-if="details" class="flex items-center gap-3 mb-4">
                <JobAvatar :name="details.name || job.company_name" :src="details.website?.logo_url || details.logo_url" />
                <div class="min-w-0 flex-1">
                  <p v-if="details.name" class="text-sm font-medium text-gray-900 dark:text-white truncate">{{ details.name }}</p>
                  <div class="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs">
                    <a v-if="details.website_url" :href="details.website_url" target="_blank" rel="noopener noreferrer" data-turbo="false"
                      class="font-medium text-blue-600 dark:text-blue-400 hover:underline truncate max-w-[14rem]">{{ websiteHost }}</a>
                    <a v-if="details.linkedin_url" :href="details.linkedin_url" target="_blank" rel="noopener noreferrer" data-turbo="false"
                      class="text-gray-500 dark:text-gray-400 hover:underline">LinkedIn</a>
                    <a v-for="link in socialLinks" :key="link.network" :href="link.url" target="_blank" rel="noopener noreferrer" data-turbo="false"
                      class="text-gray-500 dark:text-gray-400 hover:underline capitalize">{{ link.network }}</a>
                  </div>
                </div>
                <div v-if="swatches.length" class="flex items-center gap-1 flex-shrink-0" role="img" :aria-label="`Brand colours: ${swatches.join(', ')}`">
                  <span v-for="hex in swatches" :key="hex" class="w-4 h-4 rounded-full ring-1 ring-black/10 dark:ring-white/20" :style="{ backgroundColor: hex }" :title="hex"></span>
                </div>
              </div>

              <dl v-if="companyFacts.length" class="grid grid-cols-2 gap-x-4 gap-y-3 mb-3">
                <div v-for="fact in companyFacts" :key="fact.label" class="min-w-0">
                  <dt class="text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">{{ fact.label }}</dt>
                  <dd class="text-sm text-gray-800 dark:text-gray-100 mt-0.5 break-words">{{ fact.value }}</dd>
                </div>
              </dl>

              <p v-if="aboutText" class="text-sm text-gray-600 dark:text-gray-300 leading-relaxed whitespace-pre-line break-words line-clamp-5">{{ aboutText }}</p>

              <div v-if="specialties.length" class="flex flex-wrap gap-1.5 mt-3">
                <span v-for="item in specialties" :key="item" :class="pill('gray')">{{ item }}</span>
              </div>

              <!-- What the company's own website says, and where that stands -->
              <p v-if="details?.website?.description" class="mt-3 pl-3 border-l-2 border-gray-200 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400 leading-relaxed line-clamp-3">
                <span class="font-medium text-gray-600 dark:text-gray-300">From their website:</span> {{ details.website.description }}
              </p>
              <p v-else-if="details?.website_status === 'pending'" class="mt-3 text-xs text-gray-400 dark:text-gray-500">Reading the company's website...</p>
              <p v-else-if="details?.website_status === 'failed'" class="mt-3 text-xs text-amber-600 dark:text-amber-400">The company's website could not be read{{ details.website_error ? `: ${details.website_error}` : '.' }}</p>
            </section>

            <section v-if="job.hiring_team" aria-labelledby="hiring-team" :class="hasCompany ? 'md:border-l md:border-gray-200 md:dark:border-gray-700 md:pl-8' : ''">
              <h3 id="hiring-team" class="text-sm font-semibold text-gray-900 dark:text-white mb-3">Hiring team</h3>
              <div class="flex items-start gap-3">
                <JobAvatar :name="job.hiring_team.name" :src="job.hiring_team.photo_url" />
                <div class="min-w-0">
                  <p class="text-sm font-semibold text-gray-900 dark:text-white">
                    {{ job.hiring_team.name }}
                    <span v-if="job.hiring_team.connection_degree" class="font-normal text-gray-400">· {{ job.hiring_team.connection_degree }}</span>
                  </p>
                  <p v-if="job.hiring_team.role" class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{{ job.hiring_team.role }}</p>
                  <p v-if="job.hiring_team.headline" class="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">{{ job.hiring_team.headline }}</p>
                  <a v-if="job.hiring_team.profile_url" :href="job.hiring_team.profile_url" target="_blank" rel="noopener noreferrer" data-turbo="false"
                    class="inline-block mt-2 text-sm font-semibold text-blue-600 dark:text-blue-400 hover:underline">View profile</a>
                </div>
              </div>
            </section>
          </div>

          <template v-if="others && others.jobs.length">
            <hr class="border-gray-200 dark:border-gray-700">
            <CompanyJobs :data="others" :company="job.company_name || 'this company'" @open="openOther" @toggle="toggleOther" />
          </template>

          <hr class="border-gray-200 dark:border-gray-700">

          <!-- Where it came from -->
          <footer class="flex flex-wrap items-center gap-x-4 gap-y-1 pt-4 text-xs text-gray-400 dark:text-gray-500">
            <span v-if="job.search.title">Found by <span class="font-medium text-gray-500 dark:text-gray-400">{{ job.search.title }}</span> in <span class="font-medium text-gray-500 dark:text-gray-400">{{ job.search.location }}</span></span>
            <span>Saved {{ savedOn }}</span>
            <span v-if="updatedOn !== savedOn">Updated {{ updatedOn }}</span>
          </footer>
        </article>
      </div>

      <!-- Write a resume for this job with AI -->
      <JobAiResume :job="job" @open-ai-settings="$emit('open-ai-settings')" />
    </template>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { formatDate, postedAgo } from 'utils/timeAgo.js'
import { parseDescription } from 'utils/jobDescription.js'
import JobAvatar from './JobAvatar.vue'
import JobMarkButton from './JobMarkButton.vue'
import JobAiResume from './JobAiResume.vue'
import CompanyJobs from './CompanyJobs.vue'
import { useFetchedJobsStore } from 'stores/fetchedJobs.js'
import IconButton from 'controllers/vue_components/shared/IconButton.vue'

const props = defineProps({
  job: { type: Object, default: null },
  loading: { type: Boolean, default: false },
  error: { type: String, default: null },
  applying: { type: Boolean, default: false },
  showBack: { type: Boolean, default: false },
})
defineEmits(['toggle-applied', 'toggle-bookmark', 'toggle-ignore', 'retry', 'back', 'open-company', 'open-ai-settings'])

const ICONS = {
  external: 'M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25',
  check: 'm4.5 12.75 6 6 9-13.5',
}

const LONG_BLOCKS = 12
const LONG_CHARS = 1400

const expanded = ref(false)
// The header is compact once the page is scrolled. It shrinks past one distance and grows back below a smaller one, so
// the page moving under it when it changes size cannot flip it back and forth.
const COMPACT_AFTER = 64
const EXPAND_BEFORE = 12
const scroller = ref(null)
const compact = ref(false)
const clip = ref(false) // the second row is clipped while it moves, never at rest (a tooltip would be cut off)
let unclip = null
watch(compact, (now) => {
  clearTimeout(unclip)
  clip.value = true
  if (!now) unclip = setTimeout(() => { clip.value = false }, 320)
})
onBeforeUnmount(() => clearTimeout(unclip))

// The room to scroll must cover what shrinking gives back (the second row of the header), or a short job would shrink,
// have nothing left to scroll, and grow again over and over.
const SPARE_ROOM = 100

function onScroll(event) {
  const { scrollTop: top, scrollHeight, clientHeight } = event.target
  if (!compact.value && top > COMPACT_AFTER && scrollHeight - clientHeight > COMPACT_AFTER + SPARE_ROOM) compact.value = true
  else if (compact.value && top < EXPAND_BEFORE) compact.value = false
}

// The logo really changes size (not a scaled copy), so it stays sharp and the text beside it follows.
const avatarClass = computed(() => [
  'transition-[width,height,border-radius,font-size] duration-300 ease-out motion-reduce:transition-none',
  compact.value ? 'w-8! h-8! rounded-lg! text-sm!' : '',
])

// Another job opens at its top, with the full header.
watch(() => props.job?.id, async () => {
  expanded.value = false
  compact.value = false
  await nextTick()
  if (scroller.value) scroller.value.scrollTop = 0
})

const blocks = computed(() => parseDescription(props.job?.description))

// The blocks grouped under their headings: [{ heading, blocks }]. What comes before the first
// heading is a group with no heading.
// Posters often type section titles in capitals ("EMPLOYMENT"), which shouts against a calm page, so a
// title in capitals is shown in sentence case. A short one ("IT", "QA") is left alone: it may be an acronym.
function tidyHeading(text) {
  const letters = text.replace(/[^A-Za-z]/g, '')
  if (letters.length <= 4 || text !== text.toUpperCase()) return text
  const lower = text.toLowerCase()
  return lower.charAt(0).toUpperCase() + lower.slice(1)
}

const groups = computed(() => {
  const result = []
  blocks.value.forEach((block) => {
    if (block.type === 'heading') result.push({ heading: tidyHeading(block.text), blocks: [] })
    else {
      if (!result.length) result.push({ heading: null, blocks: [] })
      result[result.length - 1].blocks.push(block)
    }
  })
  return result
})

// About how long it takes to read, at 200 words a minute. Shown only when it is worth saying.
const readingMinutes = computed(() => {
  const words = blocks.value.reduce((total, block) => {
    const text = block.type === 'list' ? block.items.join(' ') : block.text
    return total + text.split(/\s+/).filter(Boolean).length
  }, 0)
  const minutes = Math.ceil(words / 200)
  return minutes >= 2 ? minutes : 0
})
const long = computed(() => blocks.value.length > LONG_BLOCKS || (props.job?.description || '').length > LONG_CHARS)
const collapsed = computed(() => long.value && !expanded.value)

// Counted from the date the job was posted to now. The text LinkedIn gave is only its age when it was fetched, so it is the fallback.
const posted = computed(() => postedAgo(props.job?.posted_at) || props.job?.posted_text)
const appliedOn = computed(() => formatDate(props.job?.applied_at) || '')

// Copies the title and the whole description as plain text (what was collected, so it pastes the same into a resume tool).
const copied = ref(false)
const copyFailed = ref(false)
let copyTimer = null

async function copyDescription() {
  const text = [props.job?.title, props.job?.description].filter((part) => part && part.trim()).join('\n\n')
  copyFailed.value = false
  try {
    await navigator.clipboard.writeText(text)
    copied.value = true
  } catch {
    copied.value = false
    copyFailed.value = true
  }
  clearTimeout(copyTimer)
  copyTimer = setTimeout(() => { copied.value = false; copyFailed.value = false }, 2000)
}
watch(() => props.job?.id, () => { copied.value = false; copyFailed.value = false })
onBeforeUnmount(() => clearTimeout(copyTimer))

// The user's other jobs at this company: asked each time a job is opened, because what was applied to changes.
const jobsStore = useFetchedJobsStore()
const others = ref(null)
const alsoApplied = computed(() => others.value?.applied || [])
async function loadOthers() {
  const id = props.job?.id
  others.value = null
  if (!id) return
  const data = await jobsStore.companyJobs(id)
  if (props.job?.id === id) others.value = data
}
watch(() => props.job?.id, loadOthers, { immediate: true })

const openOther = (id) => jobsStore.openFromLink(id)

// Bookmarks or ignores one of those jobs from here. It changes on screen at once, and is put right again if the server says no.
async function toggleOther(item, mark) {
  const before = { ...item }
  const field = mark === 'bookmarked' ? 'bookmarked_at' : 'ignored_at'
  const other = mark === 'bookmarked' ? 'ignored_at' : 'bookmarked_at'
  const turningOn = !item[field]
  item[field] = turningOn ? new Date().toISOString() : null
  if (turningOn) item[other] = null // a job is either bookmarked or ignored, never both

  const result = await jobsStore.setMark(item.id, mark, turningOn, before)
  if (!result.ok) loadOthers()
}

// "Applied to N other jobs here", with their titles for the tooltip.
const appliedHint = computed(() => alsoApplied.value.map((item) => item.title || 'Untitled job').join(', '))
const alsoAppliedOn = computed(() => formatDate(alsoApplied.value[0]?.applied_at) || '')
const savedOn = computed(() => formatDate(props.job?.created_at) || '')
const updatedOn = computed(() => formatDate(props.job?.updated_at) || '')

// The company as stored once for all its jobs (job_companies): what LinkedIn said, and what its website says.
const details = computed(() => props.job?.company_details || null)
const companyId = computed(() => details.value?.id || null) // set when the company is stored, which is when it has a page of details
const linkedin = computed(() => details.value?.linkedin || null)

const companyFacts = computed(() => {
  const company = props.job?.company || {}
  const stored = linkedin.value || {}
  return [
    { label: 'Industry', value: stored.industry || company.industry },
    { label: 'Size', value: stored.size || company.size },
    { label: 'Headquarters', value: stored.headquarters },
    { label: 'Founded', value: stored.founded },
    { label: 'Followers', value: stored.followers || company.followers },
    { label: 'On LinkedIn', value: stored.employees_on_linkedin || company.employees_on_platform },
  ].filter((fact) => fact.value)
})
const aboutText = computed(() => linkedin.value?.about || props.job?.company?.about || null)
const specialties = computed(() => linkedin.value?.specialties || [])
const socialLinks = computed(() => (details.value?.website?.social || []).filter((link) => link.network !== 'linkedin'))

// The website's colour scheme: its theme colour, then the colours it names as its own, then the ones it uses most.
const swatches = computed(() => {
  const colors = details.value?.website?.colors
  if (!colors) return []
  return [...new Set([colors.theme_color, ...(colors.brand || []), ...(colors.palette || [])].filter(Boolean))].slice(0, 6)
})

const websiteHost = computed(() => {
  try {
    return new URL(details.value.website_url).hostname.replace(/^www\./, '')
  } catch {
    return details.value?.website_url
  }
})

const hasCompany = computed(() => companyFacts.value.length > 0 || Boolean(aboutText.value) || Boolean(details.value))

function pill(tone) {
  const base = 'px-2.5 py-1 rounded-full text-xs font-semibold '
  return base + (tone === 'blue'
    ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
    : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300')
}
</script>
